import {
  faAngleDown,
  faCircleInfo,
  faEye,
  faPen,
  faTrash,
  faUserShield,
} from "@fortawesome/pro-regular-svg-icons";
import {
  Accordion,
  Button,
  Dropdown,
  Icon,
  Label,
  Message,
  Tooltip,
} from "@intility/bifrost-react";
import { useRef, useState } from "react";
import { useClient } from "../api/clients";
import type { ClientListItem } from "../api/types";
import { formatCreatedDate, KNOWN_STACKS } from "../lib/clientTypes";
import { relationBadgeState } from "../lib/relationBadges";
import { roleIcon } from "../lib/roles";
import { ClientApiAccess } from "./ClientApiAccess";
import { ClientDataAccess } from "./ClientDataAccess";
import { ClientDeleteModal } from "./ClientDeleteModal";
import { ClientDetailRow } from "./ClientDetailRow";
import { ClientEditDrawer } from "./ClientEditDrawer";
import { ClientManagement } from "./ClientManagement";
import { ClientManifestDrawer } from "./ClientManifestDrawer";
import { ClientSecret } from "./ClientSecret";
import { ClientTags } from "./ClientTags";
import { ClientTypeBadge } from "./ClientTypeBadge";
import { ClientWireUp } from "./ClientWireUp";
import { ClientWorkloadIdentity } from "./ClientWorkloadIdentity";
import type { ManagementAccessHandle } from "./management/ManagementAccessSection";
import { useCanEditClient, useCanManageClient } from "./useCanEditClient";
import "./ClientObject.css";

// The one visual for a client, reused across the portal. Standalone it is
// 1200px wide; `fill` lets it take whatever width its container gives it.
export function ClientObject({
  client,
  fill = false,
  platformThemeClass,
  onDeleted,
}: {
  client: ClientListItem;
  fill?: boolean;
  /** Called once the client is deleted, so a page that shows only this client
   *  can leave it. */
  onDeleted?: () => void;
  /** The theme class of the platform the client is shown in. Parts that should
   *  keep the platform's colour wear it, even when a disabled client switches
   *  the rest of the object to the neutral theme. */
  platformThemeClass?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [manifestOpen, setManifestOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const manageRef = useRef<ManagementAccessHandle>(null);
  const canEdit = useCanEditClient(client);
  const canManage = useCanManageClient(client);
  const { data: detail } = useClient(client.id);
  // Known once the full record loads; a client is enabled until it says not.
  const disabled = detail?.enabled === false;
  // The user's own access to this client, for the read-only notice.
  const role = detail?.userRole ?? client.userRole;
  // The tech stack (ADR-0060): read-only here, changed in the edit drawer.
  // The full record is the fresh source once loaded; the list item carries it
  // as the fallback while that loads.
  const stack = detail ? detail.stack : client.stack;
  // A client of the custom type is managed by the Intility ID team (ADR-0044),
  // so it has no manifest to show.
  const hasManifest = client.appType !== "custom";

  return (
    <div
      className={`${fill ? "client-object client-object-fill" : "client-object"}${
        // A disabled client drops the platform's theme colour for the neutral
        // (gray) theme.
        disabled ? " bf-theme-gray" : ""
      }`}
    >
      {/* A split button, set up like the one on My Hub: Edit client is the
          default, and the arrow opens every action with its icon. */}
      <div className="client-object-actions">
        {!canEdit ? (
          // Nobody who cannot edit gets the actions: they see who can, and what
          // they have themselves, in the colour of their access. They can still
          // read the manifest.
          <div className="client-readonly-actions">
            <Message state={relationBadgeState(role, "management")}>
              Only owners and maintainers can edit. Your access:{" "}
              <Icon icon={roleIcon(role)} marginRight />
              {role}
            </Message>
            {/* In the platform's theme colour, text and hover fill. */}
            {hasManifest && (
              <Button
                variant="flat"
                className={platformThemeClass}
                onClick={() => setManifestOpen(true)}
              >
                <Icon icon={faEye} marginRight />
                View client manifest
              </Button>
            )}
          </div>
        ) : (
          <div className="client-split">
            <Button
              variant="filled"
              className="client-split-main"
              disabled={!canEdit}
              title={canEdit ? undefined : "You can't edit this client"}
              onClick={() => setEditing(true)}
            >
              <Icon icon={faPen} style={{ marginRight: 8 }} />
              Edit client
            </Button>
            <Dropdown
              visible={actionsOpen}
              onHide={() => setActionsOpen(false)}
              placement="bottom-end"
              content={
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setActionsOpen(false);
                      if (canEdit) setEditing(true);
                    }}
                  >
                    <Dropdown.Item
                      icon={faPen}
                      state={canEdit ? undefined : "inactive"}
                    >
                      Edit client
                    </Dropdown.Item>
                  </button>
                  {hasManifest && (
                    <button
                      type="button"
                      onClick={() => {
                        setActionsOpen(false);
                        setManifestOpen(true);
                      }}
                    >
                      <Dropdown.Item icon={faEye}>
                        Client manifest
                      </Dropdown.Item>
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setActionsOpen(false);
                      if (canManage) setDeleting(true);
                    }}
                  >
                    <Dropdown.Item
                      icon={faTrash}
                      state={canManage ? "alert" : "inactive"}
                    >
                      Delete client
                    </Dropdown.Item>
                  </button>
                </>
              }
            >
              <Button
                variant="filled"
                className="client-split-chevron"
                aria-label="More client actions"
                onClick={() => setActionsOpen((v) => !v)}
              >
                <Icon icon={faAngleDown} />
              </Button>
            </Dropdown>
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 16,
        }}
      >
        <ClientTypeBadge
          appType={client.appType}
          purposeTag={client.purposeTag}
        />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {/* The user's own access, as the icon of the role (the same icons as
            in the Management section); the tooltip says which. */}
        <Tooltip content={`Your access: ${role}`}>
          <span
            className="bf-large"
            role="img"
            aria-label={`Your access: ${role}`}
            style={{ display: "inline-flex" }}
          >
            <Icon icon={roleIcon(role)} />
          </span>
        </Tooltip>
        <h3 className="bf-h3" style={{ margin: 0 }}>
          {client.name}
        </h3>
      </div>
      {/* The description is on the full record, so it appears once that loads;
          a client without one shows nothing. */}
      {detail?.description && (
        <p className="bf-p bfc-base-2" style={{ margin: "4px 0 0" }}>
          {detail.description}
        </p>
      )}
      <div style={{ marginTop: 16 }}>
        <ClientTags client={client} />
      </div>
      {disabled && (
        <Message
          state={canEdit ? "alert" : "neutral"}
          style={{ marginTop: 16 }}
        >
          Client is disabled
        </Message>
      )}
      <div className="client-object-boxes">
        <div className="client-object-box">
          <span className="client-object-created">
            Created {formatCreatedDate(client.createdAt)}
          </span>
          <ClientDetailRow
            label="Client ID"
            hint="The client's OAuth identifier (client_id): its own credential, and how it is referenced as a principal in authorization checks (client:<clientId>)."
            value={client.clientId}
          />
          <ClientDetailRow
            label="Object ID"
            hint="Keycloak's internal UUID for this client. Used when managing the client itself, for example as the target when granting ownership."
            value={client.id}
          />
          {/* The tech stack (ADR-0060): what the client's application is built
              with. Read-only here; the Edit client drawer changes it, and the
              wire-up drawer's prompt and steps follow it. */}
          <div>
            <div className="client-detail-row-label">
              <Label>Tech stack</Label>
              <Tooltip content="What the client's application is built with. A stack with an SDK gets a setup guide in the wire-up drawer; any other stack builds the configuration itself.">
                <button
                  type="button"
                  className="client-detail-row-hint"
                  aria-label="About Tech stack"
                >
                  <Icon icon={faCircleInfo} />
                </button>
              </Tooltip>
            </div>
            <div className="client-detail-row-value">
              {stack ? (
                <span>
                  {KNOWN_STACKS.find((s) => s.value === stack.toLowerCase())
                    ?.label ?? stack}
                </span>
              ) : (
                <span className="bfc-base-c-2" style={{ fontStyle: "italic" }}>
                  Not set
                </span>
              )}
            </div>
          </div>
        </div>
        {/* A client has one credential: SPIFFE clients get a workload identity,
            clients with a secret get the secret, in the same place. */}
        <ClientWorkloadIdentity client={client} />
        <ClientSecret client={client} />
      </div>
      {/* Wire up (login or authentication, by the client's kind) above API
          access on the left, Data access beside both, as tall as the two
          together. */}
      <div className="client-object-grid">
        <div className="client-object-stack">
          <ClientWireUp client={client} />
          <ClientApiAccess client={client} />
        </div>
        <ClientDataAccess client={client} />
      </div>
      <div className="client-object-accordion">
        <Accordion variant="styled">
          {/* eager: the section stays mounted while collapsed, so Manage
              access works from the header without opening the accordion. */}
          <Accordion.Item
            title="Management"
            icon={faUserShield}
            noPadding
            eager
            actions={
              <Tooltip content="Manage access">
                <Button
                  variant="flat"
                  small
                  className="client-icon-button"
                  aria-label="Manage access"
                  disabled={!canEdit}
                  onClick={() => manageRef.current?.openManage()}
                >
                  <Icon icon={faPen} />
                </Button>
              </Tooltip>
            }
          >
            <ClientManagement client={client} manageRef={manageRef} />
          </Accordion.Item>
        </Accordion>
      </div>
      <ClientEditDrawer
        client={client}
        open={editing}
        onClose={() => setEditing(false)}
      />
      <ClientManifestDrawer
        client={client}
        open={manifestOpen}
        onClose={() => setManifestOpen(false)}
      />
      <ClientDeleteModal
        client={client}
        open={deleting}
        onClose={() => setDeleting(false)}
        onDeleted={onDeleted}
      />
    </div>
  );
}
